import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Autocomplete, 
  CircularProgress, 
  Card, 
  CardContent,
  Box,
  Typography
} from '@mui/material';
import { Search as SearchIcon, Person } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { searchUsers } from '../../services/userService';

const UserSearch = ({ onUserSelect }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (searchTerm.length < 3) {
      setOptions([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setLoading(true);
      try {
        const users = await searchUsers(searchTerm);
        setOptions(users.data);
      } catch (error) {
        console.error('Error searching users:', error);
      }
      setLoading(false);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  return (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
      border: '1px solid rgba(0, 38, 66, 0.06)',
      mb: 3
    }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 }, 
          mb: { xs: 2, sm: 2.5 }
        }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SearchIcon sx={{ color: '#FFFFFF', fontSize: '1.2rem' }} />
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#002642',
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}
          >
            Buscar Usuario
          </Typography>
        </Box>

        <Autocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(event, value) => onUserSelect(value)}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          options={options}
          loading={loading}
          renderOption={(props, option) => (
            <Box 
              component="li" 
              {...props}
              sx={{
                '&[aria-selected="true"]': {
                  backgroundColor: 'rgba(0, 38, 66, 0.08) !important'
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 38, 66, 0.04) !important'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Person sx={{ color: '#DB9600', fontSize: '1.1rem' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#002642' }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#00577A' }}>
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Buscar por nombre o email (mínimo 3 caracteres)"
              variant="outlined"
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(0, 38, 66, 0.2)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#002642'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#002642'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#002642',
                  '&.Mui-focused': {
                    color: '#002642'
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#002642',
                  opacity: 0.7
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      style={{ 
                        color: '#002642', 
                        fontSize: '0.9rem' 
                      }} 
                    />
                  </Box>
                ),
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress 
                        color="inherit" 
                        size={20} 
                        sx={{ color: '#002642' }}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </CardContent>
    </Card>
  );
};

export default UserSearch;